import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import type { TFunction } from 'i18next';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { usePaymentMutations } from '../hooks';
import type { Payment } from '../types';

const editPaymentSchema = (t: TFunction) =>
  z.object({
    description: z.string().optional(),
    amount: z
      .number({ required_error: t('validation.amountRequired') })
      .min(1, t('validation.amountRequired')),
    paidAt: z.date().optional(),
    clientId: z.number().optional(),
    paymentMethodId: z.number().optional(),
  });

type EditPaymentFormData = z.infer<ReturnType<typeof editPaymentSchema>>;

interface EditPaymentDialogProps {
  payment: Payment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Edit a payment in any status. The bill and contract are deliberately absent:
// re-pointing a payment at another bill is an allocation move handled by
// AttachPaymentBillDialog, and the server rejects those fields here.
export function EditPaymentDialog({
  payment,
  open,
  onOpenChange,
}: EditPaymentDialogProps) {
  const { t } = useTranslation();
  const { updateMutation } = usePaymentMutations();

  const schema = useMemo(() => editPaymentSchema(t), [t]);

  const form = useForm<EditPaymentFormData>({
    resolver: zodResolver(schema),
    values: {
      description: payment?.description ?? '',
      amount: payment?.amount,
      paidAt: payment?.paidAt ? new Date(payment.paidAt) : undefined,
      clientId: payment?.clientId ?? undefined,
      paymentMethodId:
        payment?.paymentMethodId ?? payment?.paymentMethod?.id ?? undefined,
    },
  });

  const onSubmit = (data: EditPaymentFormData) => {
    updateMutation.mutate(
      {
        id: payment.id,
        data: { ...data, paidAt: data.paidAt?.toISOString() },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const isSubmitting = updateMutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="p-6 max-w-lg">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {t('edit')} {t('payment')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => onOpenChange(false)} aria-label="Close dialog">
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 flex flex-col gap-4"
            noValidate
          >
            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('description')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('enterDescription')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount */}
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('amount')} {payment?.currency === 'USD' ? '$' : 'UZS'} *
                  </FormLabel>
                  <FormControl>
                    <NumericFormat
                      thousandSeparator=" "
                      allowNegative={false}
                      decimalScale={0}
                      customInput={Input}
                      placeholder="123 000"
                      value={field.value ?? ''}
                      onValueChange={(values) =>
                        field.onChange(values.floatValue ?? undefined)
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    {t('editPaymentAmountHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Paid At */}
            <FormField
              control={form.control}
              name="paidAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paidAt')}</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? undefined)}
                      mode="datetime"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment Method */}
            <FormField
              control={form.control}
              name="paymentMethodId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paymentMethod')}</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/payment-methods"
                      labelKey="name"
                      valueKey="id"
                      selectedId={field.value}
                      onSelectItem={(item) => field.onChange(item?.['id'] ?? null)}
                      placeholder={t('selectMethod')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Client */}
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('client')}</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/clients"
                      labelKey="name"
                      labelKeys={['lastname', 'name', 'middlename']}
                      valueKey="id"
                      selectedId={field.value}
                      onSelectItem={(item) => field.onChange(item?.['id'] ?? null)}
                      placeholder={t('selectClient')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => onOpenChange(false)}>
                {t('cancel')}
              </AlertDialogCancel>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? t('saving') : t('save')}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
